import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronUp, ChevronDown, Check, Compass, BarChart3, RefreshCw, Heart, Sparkles, Target, BookOpen, Circle, TrendingUp } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from "recharts";
import { Button } from "@/components/ui/button";
import { apiCall } from "../App";
import { toast } from "sonner";

const PILLAR_COLORS = {
  P1: { bg: "bg-red-500/10", border: "border-red-500/30", text: "text-red-400", fill: "#EF4444" },
  P2: { bg: "bg-orange-500/10", border: "border-orange-500/30", text: "text-orange-400", fill: "#F97316" },
  P3: { bg: "bg-amber-500/10", border: "border-amber-500/30", text: "text-amber-400", fill: "#F59E0B" },
  P4: { bg: "bg-emerald-500/10", border: "border-emerald-500/30", text: "text-emerald-400", fill: "#10B981" },
  P5: { bg: "bg-teal-500/10", border: "border-teal-500/30", text: "text-teal-400", fill: "#14B8A6" },
  P6: { bg: "bg-cyan-500/10", border: "border-cyan-500/30", text: "text-cyan-400", fill: "#06B6D4" },
  P7: { bg: "bg-blue-500/10", border: "border-blue-500/30", text: "text-blue-400", fill: "#3B82F6" },
  P8: { bg: "bg-indigo-500/10", border: "border-indigo-500/30", text: "text-indigo-400", fill: "#6366F1" },
  P9: { bg: "bg-purple-500/10", border: "border-purple-500/30", text: "text-purple-400", fill: "#A855F7" },
  P10: { bg: "bg-fuchsia-500/10", border: "border-fuchsia-500/30", text: "text-fuchsia-400", fill: "#D946EF" },
  P11: { bg: "bg-pink-500/10", border: "border-pink-500/30", text: "text-pink-400", fill: "#EC4899" },
  P12: { bg: "bg-rose-500/10", border: "border-rose-500/30", text: "text-rose-400", fill: "#F43F5E" },
};


const PillarSection = ({ pillar }) => {
  const [expanded, setExpanded] = useState(false);
  const colors = PILLAR_COLORS[pillar.pillar_code] || PILLAR_COLORS.P1;

  return (
    <div
      className={`rounded-xl border ${colors.border} overflow-hidden`}
      data-testid={`pillar-${pillar.pillar_code}`}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between p-4 ${colors.bg} hover:opacity-90 transition-opacity`}
        data-testid={`pillar-toggle-${pillar.pillar_code}`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-2 h-8 rounded-full ${colors.accent}`} />
          <div className="text-left">
            <span className={`font-heading text-base ${colors.text}`}>
              {pillar.pillar_code}: {pillar.pillar_name}
            </span>
            <span className="text-chakra-crown/50 text-xs ml-2">({pillar.pillar_english})</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs px-2 py-0.5 rounded-full bg-cosmic-deep text-chakra-crown/70">
            {pillar.trigger_count} matched
          </span>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-chakra-crown/50" />
          ) : (
            <ChevronDown className="w-4 h-4 text-chakra-crown/50" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-3 py-2 space-y-2">
          {pillar.triggers.map((trigger) => (
            <div
              key={trigger.id}
              className="p-2 rounded-lg bg-cosmic-light/10 border border-divine-gold/5"
              data-testid={`trigger-card-${trigger.id}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-divine-gold font-medium text-lg">
                  {trigger.name}
                </span>
                <div className="flex items-center gap-2 text-base">
                  <span className="px-2 py-0.5 rounded bg-divine-gold/10 text-divine-gold-light">
                    {trigger.abhideya}
                  </span>
                  <span className="text-chakra-crown/60">{trigger.gita_verse}</span>
                </div>
              </div>
              {/* Guna split bar */}
              {trigger.guna_split && (
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex-1 h-1 bg-cosmic-deep rounded-full overflow-hidden flex">
                    <div className="bg-chakra-root h-full" style={{ width: `${trigger.guna_split.rajas}%` }} />
                    <div className="bg-cosmic-light h-full" style={{ width: `${trigger.guna_split.tamas}%` }} />
                    <div className="bg-chakra-heart h-full" style={{ width: `${trigger.guna_split.sattva}%` }} />
                  </div>
                  <span className="text-sm text-chakra-crown/50 whitespace-nowrap">
                    {trigger.guna_flow}
                  </span>
                </div>
              )}
              {/* Perception pair */}
              <div className="grid grid-cols-2 gap-2 mt-1">
                <div className="p-2 rounded-md bg-chakra-root/5 border border-chakra-root/10">
                  <span className="text-xs text-chakra-root/70 uppercase tracking-wide block mb-0.5">False Perception</span>
                  <span className="text-chakra-root text-base font-medium">{trigger.false_perception}</span>
                </div>
                <div className="p-2 rounded-md bg-chakra-heart/5 border border-chakra-heart/10">
                  <span className="text-xs text-chakra-heart/70 uppercase tracking-wide block mb-0.5">Real Perception</span>
                  <span className="text-chakra-heart text-base font-medium">{trigger.real_perception}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PracticeCard = ({ practice, triggerLabel, onToggle }) => (
  <div className={`p-4 rounded-xl border transition-all ${
    practice.completed
      ? "bg-chakra-heart/10 border-chakra-heart/30"
      : "bg-cosmic-light/10 border-divine-gold/10 hover:border-divine-gold/30"
  }`}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <button
          onClick={() => onToggle(practice.id, practice.completed)}
          className={`w-6 h-6 rounded flex items-center justify-center transition-colors border-2 shrink-0 ${
            practice.completed ? "bg-chakra-heart border-chakra-heart" : "border-divine-gold/40 hover:border-divine-gold"
          }`}
          data-testid={`practice-${practice.id}`}
        >
          {practice.completed && <Check className="w-4 h-4 text-cosmic-void" />}
        </button>
        <div>
          <p className={`font-medium ${practice.completed ? "text-chakra-heart" : "text-divine-gold"}`}>
            {practice.name} <span className="text-chakra-crown/60 font-normal">({practice.english})</span>
          </p>
          <p className="text-chakra-crown/60 text-sm">{practice.action}</p>
          {triggerLabel && (
            <p className="text-divine-gold/50 text-xs mt-1">Linked to: {triggerLabel}</p>
          )}
        </div>
      </div>
      <span className="text-chakra-crown/40 text-sm shrink-0">{practice.duration} min</span>
    </div>
  </div>
);

const AlignmentSection = ({ title, count, countLabel, testId, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="sacred-card" data-testid={testId}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between" data-testid={`${testId}-toggle`}>
        <h3 className="font-heading text-lg text-divine-gold">{title}</h3>
        <div className="flex items-center gap-2">
          {count !== null && <span className="text-xs text-chakra-crown/50">{count} {countLabel}</span>}
          {open ? <ChevronUp className="w-4 h-4 text-divine-gold/50" /> : <ChevronDown className="w-4 h-4 text-divine-gold/50" />}
        </div>
      </button>
      {open && <div className="mt-4 space-y-4">{children}</div>}
    </div>
  );
};

const PipelineContent = ({ items, fields, emptyMsg, sectionId, completedItems, onToggle }) => {
  if (!items || items.length === 0 || items.every(i => fields.every(f => !i[f.key]))) {
    return <p className="text-chakra-crown/50 text-center py-4 text-sm">{emptyMsg}</p>;
  }
  const hasContent = items.filter(i => fields.some(f => i[f.key]));
  if (hasContent.length === 0) return <p className="text-chakra-crown/50 text-center py-4 text-sm">{emptyMsg}</p>;
  return (
    <div className="space-y-3">
      {hasContent.map((item, idx) => {
        const itemKey = sectionId ? `${sectionId}_${idx}` : null;
        const isChecked = itemKey && completedItems ? completedItems.includes(itemKey) : false;
        return (
          <div key={idx} className={`rounded-xl border p-3.5 space-y-2 transition-all ${isChecked ? "bg-chakra-heart/10 border-chakra-heart/30" : "bg-purple-500/5 border-purple-500/10"}`}>
            <div className="flex items-start gap-3">
              {onToggle && itemKey && (
                <button onClick={() => onToggle(itemKey)}
                  className={`w-5 h-5 mt-0.5 rounded flex items-center justify-center transition-colors border-2 shrink-0 ${isChecked ? "bg-chakra-heart border-chakra-heart" : "border-divine-gold/40 hover:border-divine-gold"}`}
                  data-testid={`pipeline-check-${itemKey}`}>
                  {isChecked && <Check className="w-3 h-3 text-cosmic-void" />}
                </button>
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-heading ${isChecked ? "text-chakra-heart" : "text-purple-300"}`}>{item._title || item.kosha_name || item.vikara_label || item.guna_label || item.label || ""}</p>
                <div className={`grid gap-2 mt-2 ${fields.length <= 2 ? "grid-cols-2" : "grid-cols-1 sm:grid-cols-" + Math.min(fields.length, 3)}`}>
                  {fields.map((f, fi) => item[f.key] ? (
                    <div key={fi} className="rounded-lg bg-cosmic-void/40 border border-purple-500/10 p-2.5">
                      <p className="text-purple-400/60 text-[10px] font-heading tracking-wider mb-1">{f.label}</p>
                      <p className="text-chakra-crown/70 text-xs leading-relaxed">{item[f.key]}</p>
                    </div>
                  ) : null)}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const VerseCard = ({ verse, triggerLabel, onToggle }) => (
  <div className={`p-4 rounded-xl border transition-all ${verse.completed ? "bg-chakra-heart/10 border-chakra-heart/30" : "bg-cosmic-light/10 border-divine-gold/10 hover:border-divine-gold/30"}`}
    data-testid={`verse-${verse.id}`}>
    <div className="flex items-start gap-4">
      <button onClick={() => onToggle(verse.id, verse.completed)}
        className={`w-6 h-6 mt-1 rounded flex items-center justify-center transition-colors border-2 shrink-0 ${verse.completed ? "bg-chakra-heart border-chakra-heart" : "border-divine-gold/40 hover:border-divine-gold"}`}
        data-testid={`verse-toggle-${verse.id}`}>
        {verse.completed && <Check className="w-4 h-4 text-cosmic-void" />}
      </button>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <p className={`label-sacred ${verse.completed ? "text-chakra-heart" : ""}`}>{verse.verse}</p>
          {triggerLabel && <span className="text-divine-gold/40 text-xs">via {triggerLabel}</span>}
        </div>
        <p className="text-divine-gold font-heading text-base mb-2">{verse.sanskrit}</p>
        <p className="text-chakra-crown italic mb-1">"{verse.translation}"</p>
        <p className="text-chakra-crown/50 text-sm">{verse.context}</p>
      </div>
    </div>
  </div>
);

const HarmonyItem = ({ item, onToggle }) => (
  <div className={`p-3 rounded-xl border transition-all ${item.completed ? "bg-chakra-heart/10 border-chakra-heart/30" : "bg-cosmic-light/10 border-divine-gold/10 hover:border-divine-gold/30"}`}>
    <div className="flex items-center gap-4">
      <button onClick={() => onToggle(item.id, item.completed)}
        className={`w-6 h-6 rounded flex items-center justify-center transition-colors border-2 shrink-0 ${item.completed ? "bg-chakra-heart border-chakra-heart" : "border-divine-gold/40 hover:border-divine-gold"}`}
        data-testid={`harmony-${item.id}`}>
        {item.completed && <Check className="w-4 h-4 text-cosmic-void" />}
      </button>
      <p className={`text-base ${item.completed ? "text-chakra-heart" : "text-chakra-crown"}`}>{item.label || item.title}</p>
    </div>
  </div>
);

const DiagnosticResultsBucket = ({ awareness, energyChartData, koshaQuizProps, token }) => {
  const navigate = useNavigate();
  const [retaking, setRetaking] = useState(false);
  const [showTriggers, setShowTriggers] = useState(false);
  const [showEnergy, setShowEnergy] = useState(false);
  const [showGuna, setShowGuna] = useState(false);

  const handleRetake12D = async () => {
    if (!window.confirm("This will reset your 12D profiling results. You'll need to retake the full assessment. Continue?")) return;
    setRetaking(true);
    const result = await apiCall("POST", "/profiling/retake", null, token);
    setRetaking(false);
    if (result.success) {
      toast.success("12D Profiling reset. Redirecting to assessment...");
      setTimeout(() => navigate("/profiling"), 500);
    } else {
      toast.error(result.error || "Failed to reset profiling");
    }
  };

  const profilingComplete = awareness?.trigger_board?.length > 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="diagnostic-awareness-container">

      {/* ===== A. DIAGNOSTIC TESTS (Left Column) ===== */}
      <div className="space-y-4" data-testid="diagnostic-tests-section">
        <div>
          <h2 className="font-heading text-xl text-divine-gold">A. DIAGNOSTIC TESTS</h2>
          <p className="text-chakra-crown/50 text-sm mt-1">Self-assessment & pattern detection quizzes</p>
        </div>

        {/* 12D Profiling Test Card */}
        <div className={`p-4 rounded-xl border ${profilingComplete ? "border-emerald-500/20 bg-emerald-500/5" : "border-amber-500/20 bg-amber-500/5"}`} data-testid="profiling-test-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {profilingComplete
                ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                : <Target className="w-5 h-5 text-amber-400" />}
              <div>
                <p className={`font-heading text-sm ${profilingComplete ? "text-emerald-400" : "text-amber-400"}`}>12D RELATIONSHIP PROFILING</p>
                <p className="text-chakra-crown/50 text-xs mt-0.5">
                  {profilingComplete ? "Completed - 12 dimensions mapped" : "Not yet completed"}
                </p>
              </div>
            </div>
            <button
              onClick={handleRetake12D}
              disabled={retaking}
              className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg border border-divine-gold/20 text-divine-gold hover:bg-divine-gold/10 transition-colors disabled:opacity-50"
              data-testid="retake-12d-profiling"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${retaking ? "animate-spin" : ""}`} />
              {retaking ? "Resetting..." : profilingComplete ? "Retake Quiz" : "Take Quiz"}
            </button>
          </div>
        </div>

        {/* Deep Dive Kosha Quiz - Test Mode */}
        <KoshaQuiz token={koshaQuizProps.token} userId={koshaQuizProps.userId} quizType={koshaQuizProps.quizType} mode="test" />
      </div>

      {/* ===== B. DIAGNOSTIC RESULTS (Right Column) ===== */}
      <div className="space-y-4" data-testid="diagnostic-results-section">
        <div>
          <h2 className="font-heading text-xl text-divine-gold">B. DIAGNOSTIC RESULTS</h2>
          <p className="text-chakra-crown/50 text-sm mt-1">Your computed analysis from completed assessments</p>
        </div>

        {/* 1. Trigger Board — collapsed */}
        <div className="sacred-card" data-testid="triggers-panel">
          <button onClick={() => setShowTriggers(!showTriggers)} className="w-full flex items-center justify-between" data-testid="triggers-toggle">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-divine-gold" />
              <h3 className="font-heading text-lg text-divine-gold">PILLAR-WISE TRIGGER BOARD</h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-chakra-crown/50">{awareness?.trigger_board?.length || 0} pillars</span>
              {showTriggers ? <ChevronUp className="w-4 h-4 text-divine-gold/50" /> : <ChevronDown className="w-4 h-4 text-divine-gold/50" />}
            </div>
          </button>
          {showTriggers && (
            <div className="mt-4 space-y-4">
              <p className="text-chakra-crown/50 text-sm">Matched triggers from your 12D profiling, grouped by pillar</p>
              {awareness?.trigger_board?.length > 0 ? (
                <div className="space-y-3">
                  {awareness.trigger_board.map((pillar) => (
                    <PillarSection key={pillar.pillar_code} pillar={pillar} />
                  ))}
                </div>
              ) : (
                <p className="text-chakra-crown/50 text-center py-6">Complete 12D profiling to see your triggers</p>
              )}
            </div>
          )}
        </div>

        {/* 2. Energy & Vikara — collapsed */}
        <div className="sacred-card" data-testid="energy-vikara-panel">
          <button onClick={() => setShowEnergy(!showEnergy)} className="w-full flex items-center justify-between" data-testid="energy-toggle">
            <div className="flex items-center gap-2">
              <Scale className="w-4 h-4 text-pink-400" />
              <h3 className="font-heading text-lg text-divine-gold">ENERGY POLARITY & VIKARA</h3>
            </div>
            {showEnergy ? <ChevronUp className="w-4 h-4 text-divine-gold/50" /> : <ChevronDown className="w-4 h-4 text-divine-gold/50" />}
          </button>
          {showEnergy && (
            <div className="mt-4 space-y-6">
              {/* Energy Polarity - Two Assessment Types */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Assessment 1: Pillar-Based Weighted Matrix */}
                <div data-testid="energy-panel-pillar">
                  <h4 className="font-heading text-base text-divine-gold mb-1">6-Pillar Weighted Matrix</h4>
                  <p className="text-chakra-crown/40 text-xs mb-3">Calculated from 12D Assessment answers</p>
                  <div className="h-[250px] min-w-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={energyChartData}>
                        <PolarGrid stroke="#ffd700" strokeOpacity={0.2} />
                        <PolarAngleAxis dataKey="subject" tick={{ fill: "#e0c3fc", fontSize: 12 }} />
                        <PolarRadiusAxis tick={false} axisLine={false} />
                        <Radar name="Energy" dataKey="A" stroke="#ffd700" fill="#ffd700" fillOpacity={0.3} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-3 text-center">
                    <div>
                      <p className="label-sacred">Masculine</p>
                      <p className="text-divine-gold text-lg">{(awareness?.energy?.masculine * 100)?.toFixed(0)}%</p>
                    </div>
                    <div>
                      <p className="label-sacred">Feminine</p>
                      <p className="text-chakra-crown text-lg">{(awareness?.energy?.feminine * 100)?.toFixed(0)}%</p>
                    </div>
                    <div>
                      <p className="label-sacred">Harmony</p>
                      <p className="text-chakra-heart text-lg">{awareness?.energy?.harmony_potential ? (awareness.energy.harmony_potential * 100).toFixed(1) : "0"}%</p>
                    </div>
                  </div>
                </div>

                {/* Assessment 2: Self-Assessment (Q8 Sliders) */}
                <div data-testid="energy-panel-self">
                  <h4 className="font-heading text-base text-divine-gold mb-1">Self-Assessment</h4>
                  <p className="text-chakra-crown/40 text-xs mb-3">Client's self-rated energy qualities (0-5 scale)</p>
                  {awareness?.energy_self_assessment ? (
                    <div className="space-y-4">
                      {/* Masculine Qualities */}
                      <div>
                        <p className="label-sacred text-xs mb-2 text-sky-400">MASCULINE QUALITIES</p>
                        <div className="space-y-2">
                          {Object.entries(awareness.energy_self_assessment.masculine || {}).map(([key, val]) => (
                            <div key={key}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-chakra-crown/70 text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                                <span className="text-sky-400 text-sm font-medium">{val}/5</span>
                              </div>
                              <div className="h-2 bg-cosmic-deep rounded-full overflow-hidden">
                                <div className="h-full bg-sky-500 transition-all duration-500 rounded-full" style={{ width: `${(val / 5) * 100}%` }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* Feminine Qualities */}
                      <div>
                        <p className="label-sacred text-xs mb-2 text-pink-400">FEMININE QUALITIES</p>
                        <div className="space-y-2">
                          {Object.entries(awareness.energy_self_assessment.feminine || {}).map(([key, val]) => (
                            <div key={key}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-chakra-crown/70 text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                                <span className="text-pink-400 text-sm font-medium">{val}/5</span>
                              </div>
                              <div className="h-2 bg-cosmic-deep rounded-full overflow-hidden">
                                <div className="h-full bg-pink-500 transition-all duration-500 rounded-full" style={{ width: `${(val / 5) * 100}%` }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-chakra-crown/40 text-sm">Self-assessment data not available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Vikara Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="lg:col-span-2" data-testid="vikara-panel">
                <h4 className="font-heading text-base text-divine-gold mb-4">Vikara Intensities</h4>
                <div className="space-y-3">
                  {[
                    { name: "Kama (Desire)", key: "kama", color: "bg-chakra-root" },
                    { name: "Krodha (Anger)", key: "krodha", color: "bg-red-500" },
                    { name: "Lobha (Greed)", key: "lobha", color: "bg-amber-500" },
                    { name: "Moha (Delusion)", key: "moha", color: "bg-purple-500" },
                    { name: "Mada (Pride)", key: "mada", color: "bg-orange-500" },
                    { name: "Matsarya (Envy)", key: "matsarya", color: "bg-emerald-500" },
                  ].map((v) => (
                    <div key={v.key}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-chakra-crown/70 text-sm">{v.name}</span>
                        <span className="text-divine-gold text-sm">{awareness?.vikara?.[v.key]?.toFixed(1) || 0}/10</span>
                      </div>
                      <div className="h-1.5 bg-cosmic-deep rounded-full overflow-hidden">
                        <div className={`h-full ${v.color} transition-all duration-500`} style={{ width: `${(awareness?.vikara?.[v.key] || 0) * 10}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          )}
        </div>

        {/* 3. Guna Distribution — collapsed */}
        <div className="sacred-card" data-testid="guna-panel">
          <button onClick={() => setShowGuna(!showGuna)} className="w-full flex items-center justify-between" data-testid="guna-toggle">
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4 text-teal-400" />
              <h3 className="font-heading text-lg text-divine-gold">GUNA DISTRIBUTION</h3>
            </div>
            {showGuna ? <ChevronUp className="w-4 h-4 text-divine-gold/50" /> : <ChevronDown className="w-4 h-4 text-divine-gold/50" />}
          </button>
          {showGuna && (
            <div className="mt-4 grid grid-cols-3 gap-6">
              {[
                { name: "Rajas", value: awareness?.guna?.rajas || 0, color: "bg-chakra-root" },
                { name: "Tamas", value: awareness?.guna?.tamas || 0, color: "bg-cosmic-light" },
                { name: "Sattva", value: awareness?.guna?.sattva || 0, color: "bg-chakra-heart" },
              ].map((guna) => (
                <div key={guna.name} className="text-center">
                  <p className="text-chakra-crown/70 mb-2">{guna.name}</p>
                  <div className="h-2 bg-cosmic-deep rounded-full overflow-hidden">
                    <div className={`h-full ${guna.color} transition-all duration-500`} style={{ width: `${guna.value}%` }} />
                  </div>
                  <p className="text-divine-gold mt-2">{guna.value?.toFixed(0)}%</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 4. Deep Dive Kosha Quiz Results */}
        <div className="sacred-card" data-testid="rel-kosha-quiz-results-section">
          <KoshaQuiz token={koshaQuizProps.token} userId={koshaQuizProps.userId} quizType={koshaQuizProps.quizType} mode="results" />
        </div>
      </div>
    </div>
  );
};


export { PILLAR_COLORS, PillarSection, PracticeCard, AlignmentSection, PipelineContent, VerseCard, HarmonyItem, DiagnosticResultsBucket };
